import type { ReactNode } from "react";

/**
 * Checks if a given ReactNode has renderable content.
 * The function evaluates whether the provided node can produce something that
 * React can render. It handles various types of nodes including strings, numbers,
 * booleans, null, undefined, arrays, and objects.
 *
 * - For `null` or `undefined`, it returns `false`.
 * - For `boolean` values, it returns `false` since React doesn't render these.
 * - For strings, it checks if the trimmed string has a non-zero length.
 * - For numbers and `bigint`, it returns `true` as they are renderable.
 * - For arrays or iterables, it performs a recursive check to see if at least
 *   one of the children has renderable content.
 * - For objects (including React elements), it treats them as renderable and
 *   returns `true`.
 *
 * @param {ReactNode} node - The ReactNode to evaluate for renderable content.
 * @returns {boolean} `true` if the node has renderable content, otherwise `false`.
 */
export const hasRenderableContent = (node: ReactNode): boolean => {
    // Handle null, undefined, or empty values
    if (node == null) return false;

    // Handle boolean values (React doesn't render these)
    if (typeof node === "boolean") return false;

    // Handle strings and numbers
    if (typeof node === "string") return node.trim().length > 0;
    if (typeof node === "number" || typeof node === "bigint") return true;

    // Handle arrays/iterables
    if (Array.isArray(node)) {
        return node.some((child: ReactNode) => hasRenderableContent(child));
    }

    // Handle React elements and other objects
    return typeof node === "object";
};
